<template>
  <div id="api_keys" class="flex flex-col gap-6 text_color">
    <div
      id="api_keys_header"
      class="flex flex-row justify-between items-center"
    >
      <h1 class="text-xl font-semibold text_color">API Keys</h1>
      <a-button class="primary_medium" @click="handleAddAPIKey"
        >Add API Key</a-button
      >
    </div>

    <div id="api_keys_body" class="flex flex-col">
      <SkeletonLoader v-if="isLoading" :page="loaderPages.TABLE" />

      <CustomTable
        v-else-if="apiKeys?.length > 0"
        empty-cell-value="--"
        :headers="apiKeyTableHeaders"
        :data="apiKeys"
        :pagination="pagination"
        :actions="apiKeyActions"
        :show-actions-column="true"
        :custom-column-render="apiKeyCustomColumnRender"
        @update:pagination="handlePageChange"
      />

      <EmptyScreen v-else source="api_keys" text="No API keys generated yet!" />
    </div>

    <AddApiKeyModal
      v-model:visible="showGenerateModal"
      @api-key-generated="apiKeyGenerated"
    />

    <DeleteApiKeyModal
      v-model:visible="showModalToDelete"
      :key-to-delete="keyToManipulate"
      @key-deleted="apiKeyDeleted"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { formatDateTime } from "@/src/utils/utils.js";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import {
  apiKeyCustomColumnRender,
  apiKeyTableHeaders,
} from "@/src/constants/integrationConstants.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import DashboardService from "@/src/api/DashboardService.js";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import AddApiKeyModal from "@/src/components/business_components/AddAPIKeyModal.vue";
import DeleteApiKeyModal from "@/src/components/business_components/DeleteAPIKeyModal.vue";

const apiKeys = ref([]);
const isLoading = ref(false);
const keyToManipulate = ref(null);
const showGenerateModal = ref(false);
const showModalToDelete = ref(false);

const pagination = ref({
  current: 1,
  pageSize: 10,
  total: 0,
});

const apiKeyActions = computed(() => [
  {
    label: "Delete",
    handler: (row) => handleAPIKey(row),
    icon: imageNames.DELETE_GREY,
  },
]);

const handlePageChange = (newPagination) => {
  try {
    pagination.value = newPagination;
    toFetchAPIKeys();
  } catch (error) {
    console.error("An error occurred while handling page change: ", error);
  }
};

const handleAddAPIKey = () => {
  try {
    showGenerateModal.value = true;
  } catch (error) {
    console.error("An error occurred while handling add API Key: ", error);
  }
};

const handleAPIKey = async (row) => {
  try {
    keyToManipulate.value = row;
    showModalToDelete.value = true;
  } catch (error) {
    console.error("An error occurred while handling API key: ", error);
  }
};

const toFetchAPIKeys = async () => {
  try {
    isLoading.value = true;

    const params = {
      page: pagination.value.current,
      page_size: pagination.value.pageSize,
    };

    const response = await DashboardService.fetchAPIKeys(params);
    if (response?.success) {
      apiKeys.value = response?.api_keys?.map((key) => ({
        ...key,
        updated_at: formatDateTime(key?.updated_at),
      }));
      pagination.value.total = response?.total || 0;
    }
  } catch (error) {
    console.error("An error occurred while fetching API Keys: ", error);
  } finally {
    isLoading.value = false;
  }
};

const apiKeyGenerated = () => {
  toFetchAPIKeys();
};

const apiKeyDeleted = () => {
  toFetchAPIKeys();
};

onMounted(() => {
  toFetchAPIKeys();
});
</script>
